// The targeting spec's 20 scripted layouts (gate B3; test data for the targets lab, not game tuning): the hero's
// place, facing and stick, the dummies (id, place, alive, priority), walls that block line of sight, and the target
// the soft chooser must pick with the game's weights (content/tables/targeting.json). Facing is a yaw: 0 faces +Z,
// π/2 faces +X. The stick is a world direction on the ground `[x, z]` (its length is the tilt). Dummy ids start at 2
// (the hero is 1). Each layout says what it tests; the ones marked "facing decides" go wrong with the facing weight
// zeroed (the planted defect `targeting-facing-zero`).

/** A wall in a layout: centre, size along x (width) and z (depth), height 2.4 m. */
export interface LayoutWall {
  x: number;
  z: number;
  w: number;
  d: number;
}

/** One dummy in a layout. */
export interface LayoutDummy {
  id: number;
  x: number;
  z: number;
  alive?: boolean;
  priority?: number;
  /** Which enemy card it wears (default by id: shade, brute, wisp in turn). */
  card?: "shade" | "brute" | "wisp";
}

/** A scripted targeting layout. */
export interface Layout {
  name: string;
  hero: { x: number; z: number; facing: number };
  stick?: [number, number];
  dummies: LayoutDummy[];
  walls?: LayoutWall[];
  /** The dummy the chooser must pick, or null for none. */
  expect: number | null;
  /** True where the facing term decides it (the planted defect must fail these). */
  facingDecides?: boolean;
}

const PI = Math.PI;

export const LAYOUTS: readonly Layout[] = [
  { name: "one dummy ahead", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 2, x: 0, z: 4 }], expect: 2 },
  { name: "the nearer of two ahead", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 2, x: 0, z: 6 }, { id: 3, x: 0.5, z: 3 }], expect: 3 },
  { name: "ahead beats a closer one behind", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 2, x: 0, z: -2.5 }, { id: 3, x: 0, z: 5 }], expect: 3, facingDecides: true },
  { name: "ahead beats a closer one beside", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 2, x: 2.5, z: 0 }, { id: 3, x: 0, z: 5.5 }], expect: 3, facingDecides: true },
  { name: "the stick picks the side", hero: { x: 0, z: 0, facing: 0 }, stick: [1, 0], dummies: [{ id: 2, x: 0, z: 4 }, { id: 3, x: 4, z: 0 }], expect: 3 },
  { name: "a stick in the dead zone steers nothing", hero: { x: 0, z: 0, facing: 0 }, stick: [0.2, 0], dummies: [{ id: 2, x: 0, z: 4 }, { id: 3, x: 4, z: 0 }], expect: 2 },
  { name: "the dead are skipped", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 2, x: 0, z: 3, alive: false }, { id: 3, x: 0, z: 6 }], expect: 3 },
  { name: "nothing alive, no target", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 2, x: 0, z: 3, alive: false }], expect: null },
  { name: "out of range, no target", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 2, x: 0, z: 9 }], expect: null },
  { name: "just inside the range", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 2, x: 0, z: 7.9 }], expect: 2 },
  { name: "priority over distance", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 2, x: 0, z: 3 }, { id: 3, x: 0, z: 5, priority: 1, card: "brute" }], expect: 3 },
  { name: "a wall hides the nearest", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 2, x: 0, z: 3 }, { id: 3, x: 4, z: 4 }], walls: [{ x: 0, z: 1.5, w: 2, d: 0.4 }], expect: 3 },
  { name: "turned to +X, the one ahead", hero: { x: 0, z: 0, facing: PI / 2 }, dummies: [{ id: 2, x: 0, z: 4 }, { id: 3, x: 5, z: 0.5 }], expect: 3, facingDecides: true },
  { name: "turned round, the one ahead over a closer one behind", hero: { x: 0, z: 0, facing: PI }, dummies: [{ id: 2, x: 0, z: -4 }, { id: 3, x: 0, z: 2 }], expect: 2, facingDecides: true },
  { name: "a tie goes to the lower id", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 3, x: -1, z: 3 }, { id: 2, x: 1, z: 3 }], expect: 2 },
  { name: "three in a row, the nearest", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 2, x: 0, z: 2 }, { id: 3, x: 0, z: 4 }, { id: 4, x: 0, z: 6 }], expect: 2 },
  { name: "the stick against a priority target", hero: { x: 0, z: 0, facing: 0 }, stick: [-1, 0], dummies: [{ id: 2, x: -4, z: 1 }, { id: 3, x: 0, z: 4, priority: 1, card: "brute" }], expect: 3, facingDecides: true },
  { name: "a wall hides everyone, no target", hero: { x: 0, z: 0, facing: 0 }, dummies: [{ id: 2, x: 0, z: 3 }, { id: 3, x: 1, z: 4 }], walls: [{ x: 0, z: 1.5, w: 6, d: 0.4 }], expect: null },
  { name: "a dummy up the bank", hero: { x: 0, z: 0, facing: PI / 2 }, dummies: [{ id: 2, x: 6.5, z: 0 }, { id: 3, x: -3, z: 0 }], expect: 2, facingDecides: true },
  {
    name: "a crowd, the stick to the front right",
    hero: { x: 0, z: 0, facing: 0 },
    stick: [-0.7, 0.7],
    dummies: [
      { id: 2, x: 3, z: 3 },
      { id: 3, x: -3, z: 3 },
      { id: 4, x: 0, z: 5 },
      { id: 5, x: 0, z: -2 },
      { id: 6, x: -6, z: 6 },
    ],
    expect: 3,
  },
];
